import React, { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TaskSelectionModal } from './task-selection-modal';
import { SlashCommandMenu } from './slash-command-menu';
import { CommandResultPopover } from './command-result-popover';
import { ScoreDisplay } from './score-display';
import { EssayScoreResult, EssayScore } from '@/app/score/types/score';
import { StructuredWritingData } from '@/types/writing';
import { Task1Type, VisualType } from '@/types/task-bank';
import { Task } from '@/app/task-bank/public-actions';
import { CommandResult } from '@/types/slash-commands';
import { SlashCommand } from '@/lib/slash-commands/registry';
import { toast } from 'sonner';

interface ReportEditorModalsProps {
  // Keyboard shortcut state
  isProUser: boolean;
  scoringRequirementsList: string[];
  isScoring: boolean;
  structuredReportData: StructuredWritingData;
  prompt: string;
  task1Type?: Task1Type;
  visualType?: VisualType;
  fetchNewScore: (
    prompt: string,
    data: StructuredWritingData,
    taskType: 'task1',
    questionType?: undefined,
    questionNote?: undefined,
    task1Type?: Task1Type,
    visualType?: VisualType
  ) => void;
  setIsScoreSheetOpen: (open: boolean) => void;

  // Slash command menu
  isSlashMenuOpen: boolean;
  slashMenuTargetElement: HTMLElement | null;
  slashMenuFilteredCommands: SlashCommand[];
  slashMenuSelectedIndex: number;
  executeSlashCommand: (commandId: string) => void;
  handleSlashClose: () => void;
  setSlashMenuSelectedIndex: (index: number) => void;

  // Command result popover
  isCommandResultPopoverOpen: boolean;
  commandResultTargetElement: HTMLElement | null;
  commandResult: CommandResult | null;
  commandError: string | null;
  isCommandLoading: boolean;
  closeCommandResultPopover: () => void;
  activeCommandId: string | null;

  // Task selection modal
  taskBankOpen: boolean;
  setTaskBankOpen: (open: boolean) => void;
  handleSelectTask: (task: Task) => void;
  selectedTaskId: string | null;

  // Minimal change dialog
  showMinimalChangeDialog: boolean;
  setShowMinimalChangeDialog: (show: boolean) => void;
  scoreResult: EssayScoreResult | null;

  // Score display
  score: EssayScore | null;
  isScoreSheetOpen: boolean;
  scoreError: string | null;
}

export function ReportEditorModals({
  isProUser,
  scoringRequirementsList,
  isScoring,
  structuredReportData,
  prompt,
  task1Type,
  visualType,
  fetchNewScore,
  setIsScoreSheetOpen,
  isSlashMenuOpen,
  slashMenuTargetElement,
  slashMenuFilteredCommands,
  slashMenuSelectedIndex,
  executeSlashCommand,
  handleSlashClose,
  setSlashMenuSelectedIndex,
  isCommandResultPopoverOpen,
  commandResultTargetElement,
  commandResult,
  commandError,
  isCommandLoading,
  closeCommandResultPopover,
  activeCommandId,
  taskBankOpen,
  setTaskBankOpen,
  handleSelectTask,
  selectedTaskId,
  showMinimalChangeDialog,
  setShowMinimalChangeDialog,
  scoreResult,
  score,
  isScoreSheetOpen,
  scoreError,
}: ReportEditorModalsProps) {
  // Effect for global Cmd+K scoring shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.shiftKey) return;
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      )
        return;

      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (isProUser && scoringRequirementsList.length === 0 && !isScoring) {
          fetchNewScore(
            prompt,
            structuredReportData,
            'task1',
            undefined,
            undefined,
            task1Type,
            visualType
          );
          setIsScoreSheetOpen(true);
        } else if (!isProUser) {
          toast.error('AI Scoring Disabled', {
            description: 'Keyboard shortcut requires a Pro account.',
          });
        } else if (scoringRequirementsList.length > 0) {
          toast.error('Cannot Score Yet', {
            description: scoringRequirementsList.join('. '),
          });
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [
    isProUser,
    scoringRequirementsList,
    isScoring,
    structuredReportData,
    prompt,
    task1Type,
    visualType,
    fetchNewScore,
    setIsScoreSheetOpen,
  ]);

  return (
    <>
      {/* Slash command menu */}
      {isSlashMenuOpen && slashMenuTargetElement && (
        <SlashCommandMenu
          targetElement={slashMenuTargetElement}
          commands={slashMenuFilteredCommands}
          selectedIndex={slashMenuSelectedIndex}
          onSelectCommand={executeSlashCommand}
          onClose={handleSlashClose}
          isOpen={isSlashMenuOpen}
          onHoverIndex={(index) => setSlashMenuSelectedIndex(index)}
        />
      )}

      {/* Command result popover */}
      {isCommandResultPopoverOpen && commandResultTargetElement && (
        <CommandResultPopover
          targetElement={commandResultTargetElement}
          result={commandResult}
          error={commandError}
          isLoading={isCommandLoading}
          onClose={closeCommandResultPopover}
          commandId={activeCommandId}
          isOpen={isCommandResultPopoverOpen}
        />
      )}

      {/* Task selection modal */}
      <TaskSelectionModal
        open={taskBankOpen}
        onOpenChange={(open: boolean) => setTaskBankOpen(open)}
        onSelectTask={handleSelectTask}
        taskType='task1'
        selectedTaskId={selectedTaskId}
      />

      {/* Minimal change error dialog */}
      {showMinimalChangeDialog && scoreResult?.changeType && (
        <AlertDialog
          open={showMinimalChangeDialog}
          onOpenChange={setShowMinimalChangeDialog}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {scoreResult.changeType === 'no_change'
                  ? 'No Changes Detected'
                  : 'Minimal Changes Detected'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {scoreResult.changeType === 'no_change'
                  ? 'Your report has not changed since the last scoring. Please make edits to get an updated score.'
                  : 'Your report has only minor changes since the last scoring. Please make more substantial edits to get an updated score.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={() => setShowMinimalChangeDialog(false)}
              >
                Okay, I&apos;ll Edit More
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Score display */}
      <ScoreDisplay
        score={score}
        isOpen={isScoreSheetOpen}
        onOpenChange={setIsScoreSheetOpen}
        isScoring={isScoring}
        scoreError={scoreError}
        taskType='task1'
        onRefresh={() => {
          if (scoringRequirementsList.length === 0) {
            fetchNewScore(
              prompt,
              structuredReportData,
              'task1',
              undefined,
              undefined,
              task1Type,
              visualType
            );
          }
        }}
      />
    </>
  );
}
